import { type ICheckList, type ICheckListItem } from "../../../interfaces";
import { checkBaseField } from "../../utils/getCheckListProgress";

export enum EChecklistType {
  ALL = "all",
  PROGRESS = "progress",
  COMPLETED = "completed",
}

export const formatChecklistSuffix = (suffix?: number) =>
  suffix != null && suffix !== 1 ? ` (${suffix})` : "";

export const checklistTypes = [
  { label: "All", value: EChecklistType.ALL },
  { label: "In progress / empty", value: EChecklistType.PROGRESS },
  { label: "Completed", value: EChecklistType.COMPLETED },
];
/**
 * Filters the provided checklists based on the specified filter type.
 *
 * @param checklists - An array of checklists to be filtered.
 * @param filter - The filter type to apply (e.g., ALL, PROGRESS, COMPLETED).
 * @returns A new array of filtered checklists with updated visibility flags.
 */
export const filterChecklists = (
  checklists: ICheckList[],
  filter: EChecklistType,
): ICheckList[] => {
  if (filter === EChecklistType.ALL) {
    // Handle ALL filter case separately for single and group checklists
    return checklists.map(checklist => {
      if (checklist.data.type === "single") {
        // For single checklists, ensure no items are hidden
        return {
          ...checklist,
          hidden: false,
        };
      } else if (checklist.data.type === "group") {
        // For group checklists, ensure no groups or items are hidden
        return {
          ...checklist,
          hidden: false,
          data: {
            ...checklist.data,
            list: checklist.data.list.map((groupItem: any) => ({
              ...groupItem,
              hidden: false,
            })),
          },
        };
      }
      // Default case (should not happen if types are correctly defined)
      return checklist;
    });
  }

  const everyCheck = (list: any) =>
    list.every((item: any) => item.unit === "header" || checkBaseField(item));

  return checklists.map(checklist => {
    if (checklist.data.type === "single") {
      // For single-type checklists, set hidden based on the filter condition
      const shouldHide =
        filter === EChecklistType.COMPLETED
          ? !everyCheck(checklist.data.list) // Hide if not all items are checked
          : everyCheck(checklist.data.list); // Hide if all items are checked

      return {
        ...checklist,
        hidden: shouldHide,
      };
    } else if (checklist.data.type === "group") {
      // For group-type checklists, process each item in the group
      const newList = checklist.data.list.map((groupItem: any) => {
        if (!groupItem.items) {
          // If the group item has no items, mark it as hidden
          return {
            ...groupItem,
            hidden: true,
          };
        }

        // Determine if the group item should be hidden
        const shouldHideGroupItem =
          filter === EChecklistType.COMPLETED
            ? !everyCheck(groupItem.items) // Hide if not all items are checked
            : everyCheck(groupItem.items); // Hide if all items are checked

        return {
          ...groupItem,
          hidden: shouldHideGroupItem,
        };
      });

      // Determine if the entire group should be hidden
      const shouldHideGroup = newList.every((item: any) => item.hidden);

      return {
        ...checklist,
        hidden: shouldHideGroup, // Hide the entire group if all items are hidden
        data: {
          ...checklist.data,
          list: newList, // Update the list with hidden flags
        },
      };
    }

    // Default case (should not happen if types are correctly defined)
    return checklist;
  });
};
/**
 * Generates a history record name for a checklist item based on its state changes.
 *
 * @param item - The checklist item to generate the history record name for.
 * @returns A string representing the history record name.
 */
export const getHistoryRecordName = (item: ICheckListItem) => {
  const getValueChangeName = () => {
    switch (item.unit) {
      case "text":
        return "Changed Text";
      case "date":
        return "Changed Date";
      case "datetime":
        return "Changed Date/Time";
      case "multiple":
        return "Changed Multi Input";
      case "check":
      default:
        return item.value ? "Checked Checkbox" : "Unchecked Checkbox";
    }
  };

  const getAvailabilityChangeName = () =>
    item.notAvailable ? "Selected Not Applicable" : "Selected Applicable";

  if (item.naChanged && !item.changed) return getAvailabilityChangeName();
  else if (item.naChanged && item.changed)
    return `${getAvailabilityChangeName()} and ${getValueChangeName()}`;
  return getValueChangeName();
};

/**
 * Clears specified flags from the provided checklists.
 *
 * @param list - An array of checklists to clear flags from.
 * @param flagsToClear - An array of flag keys to be removed from checklist items.
 * @returns A new array of checklists with the specified flags cleared.
 */
const listHasProgress = (list: ICheckListItem[]): boolean =>
  list.some(item =>
    item.text
      ? checkBaseField(item) === 1
      : (item as ICheckListItem & { items?: ICheckListItem[] }).items?.some(
          subItem => checkBaseField(subItem) === 1,
        ),
  );

/**
 * Returns true if the checklist has at least one item with user progress.
 */
export const hasChecklistProgress = (checklist: ICheckList): boolean => {
  const { list, type } = checklist.data;
  if (type === "single") return listHasProgress(list);
  if (type === "group") {
    return list.some(
      (group: ICheckListItem & { items?: ICheckListItem[] }) =>
        group.items && listHasProgress(group.items),
    );
  }
  return false;
};

/**
 * Returns true when the template is newer than the entry checklist.
 */
export const shouldSyncChecklist = (
  entry: ICheckList,
  template: ICheckList,
): boolean => Number(template.version) > Number(entry.version);

const findMatchingEntryItem = (
  entryItems: ICheckListItem[],
  templateItem: ICheckListItem,
  index: number,
): ICheckListItem | undefined => {
  if (templateItem.id) {
    const byId = entryItems.find(
      entryItem => entryItem.id && entryItem.id === templateItem.id,
    );
    if (byId && byId.unit === templateItem.unit) return byId;
  }

  const byIndex = entryItems[index];
  if (byIndex && byIndex.unit === templateItem.unit) return byIndex;

  return undefined;
};

const overlayEntryProgress = (
  templateItem: ICheckListItem,
  entryItem: ICheckListItem,
): ICheckListItem => {
  const merged: ICheckListItem = {
    ...structuredClone(templateItem),
    value: entryItem.value,
    notAvailable: entryItem.notAvailable ?? false,
    ...(entryItem.timestamp !== undefined
      ? { timestamp: entryItem.timestamp }
      : {}),
  };

  if (
    templateItem.unit === "multiple" &&
    Array.isArray(templateItem.fields) &&
    Array.isArray(entryItem.fields)
  ) {
    merged.fields = mergeItemFields(entryItem.fields, templateItem.fields);
  }

  return merged;
};

const mergeItemFields = (
  entryFields: ICheckListItem[],
  templateFields: ICheckListItem[],
): ICheckListItem[] =>
  templateFields.map((templateField, fieldIndex) => {
    const entryField = findMatchingEntryItem(
      entryFields,
      templateField,
      fieldIndex,
    );
    if (!entryField) return structuredClone(templateField);

    return overlayEntryProgress(templateField, entryField);
  });

const mergeItemsList = (
  entryItems: ICheckListItem[],
  templateItems: ICheckListItem[],
): ICheckListItem[] =>
  templateItems.map((templateItem, index) => {
    const entryItem = findMatchingEntryItem(entryItems, templateItem, index);
    if (!entryItem) return structuredClone(templateItem);

    return overlayEntryProgress(templateItem, entryItem);
  });

const findMatchingEntryGroup = (
  entryGroups: Array<ICheckListItem & { items?: ICheckListItem[] }>,
  templateGroup: ICheckListItem & { items?: ICheckListItem[] },
  index: number,
): (ICheckListItem & { items?: ICheckListItem[] }) | undefined => {
  if (templateGroup.name) {
    const byName = entryGroups.find(group => group.name === templateGroup.name);
    if (byName?.items) return byName;
  }

  const byIndex = entryGroups[index];
  if (byIndex?.items) return byIndex;

  return undefined;
};

/**
 * Merges a fresh checklist template into an entry checklist, preserving user
 * progress on matching items while applying name, description, and structural changes.
 */
export const mergeChecklistWithTemplate = (
  entry: ICheckList,
  template: ICheckList,
): ICheckList => {
  const merged: ICheckList = {
    ...entry,
    name: template.name,
    description: template.description,
    version: template.version,
    data: {
      ...entry.data,
      type: template.data.type,
      list: [],
    },
  };

  if (template.data.type === "single") {
    merged.data.list = mergeItemsList(entry.data.list, template.data.list);
  } else if (template.data.type === "group") {
    const entryGroups = entry.data.list as Array<
      ICheckListItem & { items?: ICheckListItem[] }
    >;

    merged.data.list = template.data.list.map(
      (templateGroup: ICheckListItem & { items?: ICheckListItem[] }, index) => {
        const entryGroup = findMatchingEntryGroup(
          entryGroups,
          templateGroup,
          index,
        );

        if (!entryGroup?.items) return structuredClone(templateGroup);

        return {
          ...structuredClone(templateGroup),
          items: mergeItemsList(entryGroup.items, templateGroup.items ?? []),
        };
      },
    );
  } else {
    merged.data = structuredClone(template.data);
  }

  return merged;
};

export const clearChecklistsFromFlags = (
  list: ICheckList[],
  flagsToClear: Array<keyof ICheckListItem>,
): ICheckList[] => {
  if (!list) return [];
  const copiedList = structuredClone(list);
  const itemParser = (item: ICheckListItem) => {
    flagsToClear.forEach(flag => {
      if (flag in item) delete item[flag];
    });
    return { ...item, notAvailable: item.notAvailable ?? false };
  };

  return copiedList.map((checklist: any) => {
    if (checklist.data.type === "single") {
      checklist.data.list = checklist.data.list.map(itemParser);
      return checklist;
    } else {
      checklist.data.list = checklist.data.list.map((child: any) => {
        return {
          ...child,
          items: child.items.map(itemParser),
        };
      });
      return checklist;
    }
  });
};
